import { Form, Alert, Skeleton, Input } from "antd";
import { Loader2 } from "lucide-react";

import { InvoiceDisplay } from "./InvoiceDisplay";

import { InvoiceHeader } from "./Invoice/InvoiceHeader";
import { InvoiceLogo } from "./Invoice/InvoiceLogo";
import { InvoiceContentPanel } from "./Invoice/InvoiceContentPanel";
import { InvoiceSidebar } from "./Invoice/InvoiceSidebar";

import { InvoiceCompanyHeaderInfo } from "../../../bitmaker/schemas/Billing/Invoice";

type Mode = "create" | "view" | "edit";

interface InvoiceManagerContentProps {
  mode: Mode;

  form: any;

  invoiceState: {
    invoice: any;
    invoiceFetchError: string | null;
    loadingInvoice: boolean;
  };

  nextInvoiceNumberState: {
    loadingNextInvoiceNumber: boolean;
    fetchNextInvoiceNumberError: string | null;
  };

  companyState: {
    headerDisplayInfo: InvoiceCompanyHeaderInfo | null;
    hasError: boolean;
    loadingCompanies: boolean;
  };

  selectedCompany: any;

  sidebarData: {
    clients: any[];
    loadingClients: boolean;
    fetchClientsError: any;
  };
}

export function InvoiceManagerContent({
  mode,
  form,
  invoiceState,
  nextInvoiceNumberState,
  companyState,
  selectedCompany,
  sidebarData,
}: InvoiceManagerContentProps) {
  const canRenderForm =
    mode === "create" || (mode === "edit" && invoiceState.invoice);

  const canRenderDisplay =
    mode === "view" && invoiceState.invoice && companyState.headerDisplayInfo;

  const isCreateLoading =
    mode === "create" &&
    (companyState.loadingCompanies ||
      sidebarData.loadingClients ||
      nextInvoiceNumberState.loadingNextInvoiceNumber);

  const isInvoiceLoading =
    (mode === "edit" || mode === "view") && invoiceState.loadingInvoice;

  const isLoading = isCreateLoading || isInvoiceLoading;
  const loadingText =
    mode === "create" ? "Preparing new invoice..." : "Loading invoice...";

  const cannotCreateInvoice =
    mode === "create" &&
    !isCreateLoading &&
    (!companyState.headerDisplayInfo ||
      companyState.hasError ||
      sidebarData.fetchClientsError ||
      !sidebarData.clients?.length ||
      nextInvoiceNumberState.fetchNextInvoiceNumberError);

  const cannotLoadInvoice =
    (mode === "view" || mode === "edit") &&
    !isInvoiceLoading &&
    invoiceState.invoiceFetchError;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl min-h-full">
        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center min-h-[300px] p-6">
            <Loader2 size={40} className="animate-spin text-gray-500 mb-4" />
            <span className="text-gray-700 text-lg mb-6 text-center">
              {loadingText}
            </span>
            <Skeleton active paragraph={{ rows: 6 }} className="mt-4" />
          </div>
        )}

        {/* Cannot create invoice (create mode) */}
        {!isLoading && cannotCreateInvoice && (
          <div className="p-6">
            {/* Failed to load company information */}
            {companyState.hasError && (
              <Alert
                type="error"
                showIcon
                message="Failed to load company information."
                description={
                  "You cannot create a new invoice right now. Please try again later."
                }
              />
            )}

            {/* No companies created */}
            {!companyState.hasError && !companyState.headerDisplayInfo && (
              <Alert
                type="warning"
                showIcon
                message="No company information available."
                description={
                  <>
                    You cannot create a new invoice right now. Please add your
                    company information in{" "}
                    <a
                      href="/billing/settings/company-info"
                      className="underline"
                    >
                      Billing → Settings → Company Information
                    </a>
                    .
                  </>
                }
              />
            )}

            {/* Failed to load clients */}
            {sidebarData.fetchClientsError && (
              <Alert
                type="error"
                showIcon
                message="Failed to load clients."
                description={
                  "You cannot create a new invoice right now. Please try again later."
                }
              />
            )}

            {/* No clients created */}
            {!sidebarData.fetchClientsError && !sidebarData.clients?.length && (
              <Alert
                type="warning"
                showIcon
                message="No clients available."
                description={
                  <>
                    You cannot create a new invoice right now. Please add your
                    clients in{" "}
                    <a href="/billing/clients" className="underline">
                      Billing → Clients
                    </a>
                    .
                  </>
                }
              />
            )}

            {/* Failed to generate invoice number */}
            {nextInvoiceNumberState.fetchNextInvoiceNumberError && (
              <Alert
                type="error"
                showIcon
                message="Failed to generate invoice number."
                description={
                  "You cannot create a new invoice right now. Please try again later."
                }
              />
            )}
          </div>
        )}

        {/* Cannot load invoice (view or edit mode) */}
        {!isLoading && cannotLoadInvoice && (
          <div className="p-6">
            <Alert
              type="error"
              showIcon
              message="Invoice could not be loaded."
              description="Please try again later."
            />
          </div>
        )}

        {/* Invoice Form (create and edit modes) */}
        {!isLoading &&
          canRenderForm &&
          !cannotCreateInvoice &&
          !cannotLoadInvoice && (
            <Form form={form} layout="vertical">
              <Form.Item name="language" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="currency" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="companyId" hidden>
                <Input />
              </Form.Item>

              <InvoiceHeader displayInfo={companyState.headerDisplayInfo} />

              <div className="px-10 pb-12">
                <InvoiceLogo />

                <div className="grid grid-cols-12">
                  <InvoiceContentPanel
                    form={form}
                    companyInfo={selectedCompany}
                    isEditMode={mode === "edit"}
                  />

                  <InvoiceSidebar
                    form={form}
                    clients={sidebarData.clients}
                    loadingClients={sidebarData.loadingClients}
                    fetchClientsError={sidebarData.fetchClientsError}
                  />
                </div>
              </div>
            </Form>
          )}

        {/* Invoice Display (view mode) */}
        {!isLoading && canRenderDisplay && (
          <InvoiceDisplay
            invoice={invoiceState.invoice}
            headerInfo={companyState.headerDisplayInfo}
          />
        )}
      </div>
    </div>
  );
}
